import {
    useUltraPlayStore,
    useBuyBonusStore,
    useDoubleChanceStore,
    useConfigurationStore,
    useSoundStore,
    ULTRA_PLAY_DOUBLE_CHANCE,
} from '@stores'
import { SOUNDS } from '@constants/sounds'
import {
    StopAutoplayClickedMessage,
    SetSpeedButtonLevelMessage,
    BuyBonusRejectedMessage,
    CollectClickedMessage,
    DoubleChanceButtonClickedMessage,
    GameUIMessageScope,
    GameUIMessageType,
} from 'game-ui-types'

const setSpeedLevel = (speedLevel: number) => {
    window.postMessage(
        { type: GameUIMessageType.SET_SPEED_BUTTON_LEVEL, speedLevel } as SetSpeedButtonLevelMessage,
        '*'
    )
}

// Reconcile the engine's Double Chance toggle to a desired on/off state (the engine flips on every
// message, so only send when it actually differs — isDoubleChanceActivated mirrors the engine).
const setEngineDoubleChance = (desired: boolean) => {
    const { isDoubleChanceActivated, setIsDoubleChanceActivated } = useDoubleChanceStore.getState()

    if (isDoubleChanceActivated === desired) return

    setIsDoubleChanceActivated(desired)
    window.postMessage(
        {
            type: GameUIMessageType.DOUBLE_CHANCE_BUTTON_CLICKED,
            scope: GameUIMessageScope.DOUBLE_CHANCE,
        } as DoubleChanceButtonClickedMessage,
        '*'
    )
    window.postMessage({ type: GameUIMessageType.COLLECT_CLICKED } as CollectClickedMessage, '*')
}

// Carry the selected feature from Ultra Play to the game, or reset the engine's state if no feature is selected.
// Buy Bonus is only kept if the session was running (PLAYING) when Ultra Play closed, and the selected feature is still available in the configuration.
// We have no mechanism to activate BB without starting it, so just selecting a feature in Ultra Play can't be carried over to the game.
// This is consistent with the behavior of the in-game Buy Bonus modal, which only makes it persistent if the player purchased it once.
const carryOverSelectionToGame = (selectedFeatureName: string, keepBuyBonus: boolean) => {
    const buyBonus = useBuyBonusStore.getState()

    if (selectedFeatureName === ULTRA_PLAY_DOUBLE_CHANCE) {
        setEngineDoubleChance(true)
        buyBonus.updateCurrentFeatureMultiplier(useConfigurationStore.getState().doubleChanceMultiplier)
        buyBonus.setMultiChoiceToggle(false)

        return
    }

    // Any non-DC selection means Double Chance must be off in the game.
    setEngineDoubleChance(false)

    if (selectedFeatureName === 'OFF') {
        buyBonus.updateCurrentFeatureMultiplier(1)
        buyBonus.setMultiChoiceToggle(false)

        return
    }

    const features = buyBonus.buyBonusConfig.features
    const index = features.findIndex((f) => f.name === selectedFeatureName)
    const feature = features[index]

    if (keepBuyBonus && feature) {
        buyBonus.setSelectedFeature(feature)
        buyBonus.setSelectedFeatureIndex(index)
        buyBonus.updateCurrentFeatureMultiplier(feature.multiplier)
        buyBonus.setMultiChoiceToggle(true)

        return
    }

    buyBonus.updateCurrentFeatureMultiplier(1)
    buyBonus.setMultiChoiceToggle(false)

    window.postMessage({ type: GameUIMessageType.BUY_BONUS_REJECTED } as BuyBonusRejectedMessage, '*')
}

// Full Ultra Play close: stop any running session, carry the feature selection over to the game,
// reset the store, restore the pre-Ultra-Play speed and close the modal. Shared by the manual close
// (the X button) and the automatic close when a feature triggers while Stop on Feature is enabled.
// Component visibility and the Extra Win toggle are restored by the modal's effects reacting to isOpen.
export const closeUltraPlay = () => {
    const { status, stop, reset, close, previousSpeedLevel, selectedBuyBonusFeatureName } = useUltraPlayStore.getState()
    const isActive = status === 'PLAYING' || status === 'PAUSED'

    // Play the same close sound as the in-game menus (settings).
    useSoundStore.getState().playEngineSound(SOUNDS.HIDE_DENOMINATIONS)

    if (isActive) {
        window.postMessage({ type: GameUIMessageType.STOP_AUTOPLAY_CLICKED } as StopAutoplayClickedMessage, '*')
        stop()
    }

    // Only a running session guarantees that the engine actually holds the selected buy bonus.
    carryOverSelectionToGame(selectedBuyBonusFeatureName, status === 'PLAYING')

    reset()

    setSpeedLevel(previousSpeedLevel)
    close()
}
